import { forwardRef, useCallback } from 'react';
import { Button, Message } from '../';

import styles from './BrowserLoginConfirmation.module.css';
import type { BrowserLoginConfirmationProps } from './BrowserLoginConfirmation.types';

/**
 * BrowserLoginConfirmation component
 *
 * Displays a confirmation screen for SSO/browser-based login flow.
 * Shows instructions and provides buttons to continue or go back.
 */
export const BrowserLoginConfirmation = forwardRef<HTMLDivElement, BrowserLoginConfirmationProps>(
  (
    {
      logoSrc,
      logoAlt = 'Logo',
      loginUrl,
      onOpenBrowser,
      onContinue,
      onBack,
      error,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    // Handle "here" link click
    const handleOpenBrowser = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (onOpenBrowser) {
          onOpenBrowser();
        } else if (loginUrl) {
          window.open(loginUrl, '_blank');
        }
      },
      [onOpenBrowser, loginUrl]
    );

    const wrapperClassNames = [styles.wrapper, className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={wrapperClassNames} data-testid={dataTestId || testId} data-id={dataId}>
        {/* Logo positioned above the white container */}
        {logoSrc && (
          <div className={styles.logo} data-testid={testId && `${testId}-logo`}>
            <img src={logoSrc} alt={logoAlt} />
          </div>
        )}

        <div className={styles.container} data-testid={testId && `${testId}-container`}>
          {/* Error message */}
          {error && (
            <Message
              variant="error"
              mode="banner"
              data-testid={testId && `${testId}-error`}
            >
              {error}
            </Message>
          )}

          {/* Instructions */}
          <div className={styles.message} data-testid={testId && `${testId}-message`}>
            <p>
              A new tab should now open automatically. If not, click{' '}
              <a href={loginUrl || '#'} onClick={handleOpenBrowser} className={styles.link}>
                here
              </a>{' '}
              to open it manually.
            </p>
            <p>
              Once you are logged in via the browser, click <strong>Continue</strong>. To return to the standard Login page, click <strong>Back</strong>.
            </p>
          </div>

          {/* Buttons */}
          <div className={styles.buttons} data-testid={testId && `${testId}-buttons`}>
            <Button
              variant="secondary"
              onClick={onBack}
              fullWidth
              data-testid={testId && `${testId}-back-button`}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={onContinue}
              fullWidth
              data-testid={testId && `${testId}-continue-button`}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

BrowserLoginConfirmation.displayName = 'BrowserLoginConfirmation';

export default BrowserLoginConfirmation;
